/**
 * PDP Manager
 *
 * Sistema centralizado para gerenciamento da PDP (Product Detail Page).
 * Gerencia galeria de imagens, variantes de cor e accordions.
 *
 * @version 1.0.0
 * @author Wicomm
 *
 * Arquitetura:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │                        PdpManager                               │
 * │                     (Orquestrador)                              │
 * ├─────────────────────────────────────────────────────────────────┤
 * │  GalleryObserver      - Ativa âncoras ao scrollar na galeria    │
 * │  ColorVariantLoader   - Busca variantes via Search API          │
 * │  SizeSelectorController - Popup de seleção de tamanho           │
 * │  AddToCartController  - Intercepta form, abre cart drawer       │
 * │  AccordionController  - Gerencia abrir/fechar accordions        │
 * │  InfoPanelController  - Push drawer de informações extras       │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * Princípios SOLID aplicados:
 * - SRP: Cada classe tem uma única responsabilidade
 * - OCP: Extensível via novos módulos, fechado para modificação
 * - DIP: Depende de data attributes, não de implementações específicas
 * - LSP: Módulos podem ser substituídos sem quebrar o manager
 */

/* ==========================================================================
   GALLERY OBSERVER
   Ativa âncoras do menu lateral ao scrollar pela galeria de imagens
   ========================================================================== */

class GalleryObserver {
  /**
   * @param {HTMLElement} section - Elemento da seção PDP
   * @param {Object} config - Configurações
   * @param {string} config.bannerSelector - Seletor dos banners
   * @param {string} config.anchorSelector - Seletor das âncoras
   * @param {number} config.threshold - Threshold do IntersectionObserver
   */
  constructor(section, config = {}) {
    this._section = section;
    this._config = {
      bannerSelector: '[id^="product-image-"]',
      anchorSelector: '.pdpMain__bannerMenu',
      threshold: 0.5,
      ...config
    };
    this._observer = null;

    this._init();
  }

  /** @private */
  _init() {
    const banners = this._section.querySelectorAll(this._config.bannerSelector);
    const anchors = this._section.querySelectorAll(this._config.anchorSelector);

    if (banners.length === 0 || anchors.length === 0) return;

    this._observer = new IntersectionObserver(
      (entries) => this._handleIntersection(entries),
      { threshold: this._config.threshold }
    );

    banners.forEach((banner) => this._observer.observe(banner));


  }

  /**
   * Callback do IntersectionObserver
   * @private
   */
  _handleIntersection(entries) {
    entries.forEach((entry) => {
      const anchor = this._section.querySelector(
        'a[href="#' + entry.target.id + '"]'
      );
      if (!anchor) return;

      if (entry.isIntersecting) {
        anchor.classList.remove('opacity-50');
        anchor.classList.add('opacity-100');
      } else {
        anchor.classList.remove('opacity-100');
        anchor.classList.add('opacity-50');
      }
    });
  }

  destroy() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }
}

/* ==========================================================================
   COLOR VARIANT LOADER
   Busca variantes de cor via Predictive Search API + Section Rendering API
   ========================================================================== */

class ColorVariantLoader {
  /**
   * @param {Object} config - Configurações
   * @param {string} config.containerSelector - Seletor do container de swatches
   * @param {string} config.sectionName - Nome da seção para Section Rendering API
   * @param {string} config.dataSelector - Seletor do JSON de cor
   * @param {number} config.searchLimit - Limite de resultados da busca
   */
  constructor(config = {}) {
    this._config = {
      containerSelector: '[data-color-variants]',
      sectionName: 'pdp-main',
      dataSelector: '[data-pdp-color-data]',
      searchLimit: 25,
      scope: null,
      ...config
    };

    var scope = this._config.scope || document;
    this._container = scope.querySelector(this._config.containerSelector);

    if (this._container) {
      this._init();
    }
  }

  /** @private */
  _init() {
    const variantId = this._container.dataset.variantId;
    const productId = parseInt(this._container.dataset.productId);

    if (!variantId) {
      this._removeSkeletons();
      return;
    }


    this._fetchSiblings(variantId, productId);
  }

  /**
   * Remove os elementos de skeleton loading do container
   * @private
   */
  _removeSkeletons() {
    if (!this._container) return;

    var skeletons = this._container.querySelectorAll('[data-color-skeleton]');
    skeletons.forEach(function (el) { el.remove(); });
  }

  /**
   * Busca produtos irmãos via Predictive Search API
   * @private
   */
  _fetchSiblings(variantId, currentProductId) {
    var searchUrl =
      '/search/suggest.json?q=' +
      encodeURIComponent(variantId) +
      '&resources[type]=product&resources[limit]=' +
      this._config.searchLimit;

    fetch(searchUrl)
      .then((r) => r.json())
      .then((data) => {
        var products =
          (data.resources &&
            data.resources.results &&
            data.resources.results.products) ||
          [];


        var siblings = products.filter(function (prod) {
          return prod.id !== currentProductId;
        });

        if (siblings.length === 0) {
          this._removeSkeletons();
          return;
        }

        var pending = siblings.length;
        var onComplete = () => {
          pending--;
          if (pending <= 0) {
            this._removeSkeletons();
          }
        };

        siblings.forEach((prod) => {
          this._fetchColorData(prod, variantId, onComplete);
        });
      })
      .catch((err) => {
        console.error('[PdpManager:Color] Erro na Search API:', err);
        this._removeSkeletons();
      });
  }

  /**
   * Busca dados de cor de um produto via Section Rendering API
   * @private
   * @param {Object} prod - Produto da Search API
   * @param {string} variantId - ID da variante para validação
   * @param {Function} onComplete - Callback chamado ao finalizar (sucesso ou erro)
   */
  _fetchColorData(prod, variantId, onComplete) {
    var sectionUrl =
      '/products/' + prod.handle + '?sections=' + this._config.sectionName;

    fetch(sectionUrl)
      .then((r) => r.json())
      .then((sections) => {
        var html = sections[this._config.sectionName];
        if (!html) { onComplete(); return; }

        var colorData = this._extractColorData(html);
        if (!colorData) { onComplete(); return; }

        if (colorData.variantId !== variantId) { onComplete(); return; }

        this._renderSwatch(colorData);
        onComplete();
      })
      .catch((err) => {
        console.error('[PdpManager:Color] Erro ao buscar cor:', err);
        onComplete();
      });
  }

  /**
   * Extrai dados de cor do HTML renderizado pela Section Rendering API
   * @private
   * @param {string} html - HTML da seção renderizada
   * @returns {Object|null} Dados de cor ou null
   */
  _extractColorData(html) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var script = doc.querySelector(this._config.dataSelector);

    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('[PdpManager:Color] Erro ao parsear JSON:', e);
      return null;
    }
  }

  /**
   * Renderiza um swatch de cor no container
   * @private
   * @param {Object} colorData - Dados de cor do produto
   */
  _renderSwatch(colorData) {
    var swatch = document.createElement('a');
    swatch.href = colorData.productUrl;
    swatch.className =
      'pdpMain__colorSwatch block w-[1.6rem] h-[1.6rem] border border-gray-300 hover:border-black transition-colors';
    swatch.title = colorData.colorName || colorData.productTitle;

    if (colorData.swatchImage) {
      swatch.innerHTML =
        '<img src="' +
        colorData.swatchImage +
        '" alt="' +
        (colorData.colorName || colorData.productTitle) +
        '" class="w-full h-full object-cover" width="16" height="16" />';
    } else {
      swatch.innerHTML =
        '<span class="pdpMain__colorSwatchEmpty block w-full h-full relative overflow-hidden">' +
        '<svg class="absolute inset-0 w-full h-full" viewBox="0 0 16 16" preserveAspectRatio="none" aria-hidden="true">' +
        '<line x1="0" y1="16" x2="16" y2="0" stroke="#d1d5db" stroke-width="1.5"/>' +
        '</svg></span>';
    }

    this._container.appendChild(swatch);
  }

  destroy() {
    this._container = null;
  }
}

/* ==========================================================================
   RELATED PRODUCT LOADER
   Busca o produto complementar via Predictive Search + Section Rendering API,
   valida pelo metafield LOOK_COMPLEMENTAR_OMS e injeta o accordion no placeholder
   ========================================================================== */

class RelatedProductLoader {
  /**
   * @param {Object} config - Configurações
   * @param {string} config.dataSelector        - Seletor do JSON com lookCode, productId e token
   * @param {string} config.placeholderSelector - Seletor do placeholder de injeção
   * @param {string} config.sectionName         - Nome da seção para Section Rendering API
   * @param {Function} config.onReady           - Callback(wrapper) após injeção bem-sucedida
   */
  constructor(config = {}) {
    this._config = {
      dataSelector: '[data-pdp-look-data]',
      placeholderSelector: '[data-related-product-placeholder]',
      sectionName: 'page-pdp-main',
      onReady: null,
      ...config
    };

    var dataEl = document.querySelector(this._config.dataSelector);
    this._placeholder = document.querySelector(this._config.placeholderSelector);

    if (!dataEl || !this._placeholder) return;

    var storefront = window.__STOREFRONT__ || {};
    this._storefrontToken = storefront.token || '';
    this._storefrontUrl = storefront.url || '';

    try {
      var data = JSON.parse(dataEl.textContent);
      this._lookCode = (data.lookCode || '').trim();
      this._productId = data.productId;
    } catch (e) {
      console.error('[PdpManager:Related] Erro ao parsear JSON:', e);
      return;
    }

    if (!this._storefrontToken) {
      this._remove();
      return;
    }

    if (this._lookCode) this._search();
  }

  /** @private — busca por lookCode via GraphQL e valida o primeiro candidato */
  _search() {
    var graphqlQuery = [
      'query SearchByCode($q: String!) {',
      '  search(query: $q, types: PRODUCT, first: 10) {',
      '    edges { node { ... on Product { id handle } } }',
      '  }',
      '}'
    ].join(' ');

    fetch(this._storefrontUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this._storefrontToken
      },
      body: JSON.stringify({ query: graphqlQuery, variables: { q: this._lookCode } })
    })
      .then((r) => r.json())
      .then((data) => {
        var edges = (data.data && data.data.search && data.data.search.edges) || [];
        var all = edges.map((e) => e.node).filter((n) => n && n.id);
        var candidates = all.filter((p) => parseInt(p.id.split('/').pop()) !== this._productId);

        if (candidates.length > 0) {
          this._validate(candidates[0], () => this._remove(), (w) => this._inject(w));
        } else {
          this._remove();
        }
      })
      .catch((err) => {
        console.error('[PdpManager:Related] Erro na busca:', err);
        this._remove();
      });
  }

  /**
   * Valida se o candidato tem o mesmo lookCode via Section Rendering API
   * e extrai o primeiro [data-product-wrapper] do HTML renderizado
   * @private
   */
  _validate(prod, onFail, onSuccess) {
    var url = '/products/' + prod.handle + '?sections=' + this._config.sectionName;

    fetch(url)
      .then((r) => r.json())
      .then((sections) => {
        var html = sections[this._config.sectionName];

        if (!html) { onFail(); return; }

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var wrapper = doc.querySelector('[data-product-wrapper]');

        if (!wrapper) { onFail(); return; }

        onSuccess(wrapper);
      })
      .catch((err) => {
        console.error('[PdpManager:Related] _validate erro:', err);
        onFail();
      });
  }

  /**
   * Injeta o accordion do produto relacionado no placeholder e chama onReady.
   * Adiciona programaticamente o comportamento de accordion ao wrapper,
   * pois o template renderiza o primeiro produto como estático (sempre aberto).
   * @private
   */
  _inject(wrapper) {
    if (!this._placeholder) return;

    // Converte o trigger div em accordion: adiciona classes, atributo e cursor
    var trigger = wrapper.querySelector('.pdpMain__accordionTrigger');
    if (trigger) {
      // Converte h1 para h2 no produto secundário
      var titleH1 = trigger.querySelector('h1.pdpMain__accordionTitle');
      if (titleH1) {
        var h2 = document.createElement('h2');
        h2.innerHTML = titleH1.innerHTML;
        h2.className = titleH1.className;
        titleH1.parentNode.replaceChild(h2, titleH1);
      }

      trigger.classList.add('accordion-trigger');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.style.cursor = 'pointer';

      // Injeta ícones +/− ao final do trigger
      var iconWrapper = document.createElement('div');
      iconWrapper.className = 'pdpMain__accordionIconWrapper';
      iconWrapper.innerHTML =
        '<span class="pdpMain__accordionIcon pdpMain__accordionIcon--plus icon-plus">' +
        '<svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<line y1="5.5" x2="11" y2="5.5" stroke="black"/>' +
        '<line x1="5.5" y1="11" x2="5.5" y2="2.18557e-08" stroke="black"/>' +
        '</svg>' +
        '</span>' +
        '<span class="pdpMain__accordionIcon pdpMain__accordionIcon--minus icon-minus">' +
        '<svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<line x1="11" y1="5.5" x2="-4.37114e-08" y2="5.5" stroke="black"/>' +
        '</svg>' +
        '</span>';
      trigger.appendChild(iconWrapper);
    }

    // Converte o content div em accordion-content fechado
    var content = wrapper.querySelector('.pdpMain__accordionContent');
    if (content) {
      content.classList.add('accordion-content');
      content.setAttribute('data-open', 'false');

      var inner = content.querySelector('.pdpMain__accordionInner');
      if (inner) inner.classList.add('accordion-inner');
    }

    // Aplica classes do accordion secundário
    wrapper.classList.add(
      'pdpMain__productAccordion--secondary',
      'border-b',
      'border-lightgray',
      'pb-[2.4rem]'
    );

    this._placeholder.replaceWith(wrapper);

    if (this._config.onReady) this._config.onReady(wrapper);
  }

  /** @private */
  _remove() {
    if (this._placeholder) this._placeholder.remove();
    this._placeholder = null;
  }

  destroy() {
    this._placeholder = null;
  }
}

/* ==========================================================================
   SIZE SELECTOR CONTROLLER
   Gerencia o popup customizado de seleção de tamanho
   ========================================================================== */

class SizeSelectorController {
  /**
   * @param {HTMLElement} section - Elemento da seção PDP
   * @param {Object} config - Configurações
   * @param {string} config.selectorContainer - Seletor do container
   * @param {string} config.optionSelector - Seletor das opções
   * @param {string} config.inputSelector - Seletor do input hidden
   * @param {string} config.mobileToggleSelector - Seletor do trigger mobile
   * @param {string} config.mobilePopupSelector - Seletor do popup mobile
   * @param {string} config.mobileLabelSelector - Seletor do label do trigger mobile
   */
  constructor(section, config = {}) {
    this._section = section;
    this._config = {
      selectorContainer: '[data-size-selector]',
      optionSelector: '[data-size-option]',
      inputSelector: '[data-size-input]',
      addToCartSelector: '[data-add-to-cart]',
      addToCartText: 'Add to Cart',
      mobileToggleSelector: '[data-size-mobile-toggle]',
      mobilePopupSelector: '[data-size-mobile-popup]',
      mobileLabelSelector: '[data-size-mobile-label]',
      ...config
    };

    this._container = this._section.querySelector(this._config.selectorContainer);
    if (!this._container) return;

    this._input = this._section.querySelector(this._config.inputSelector);
    this._addToCart = this._section.querySelector(this._config.addToCartSelector);
    this._mobileToggle = this._container.querySelector(this._config.mobileToggleSelector);
    this._mobilePopup = this._container.querySelector(this._config.mobilePopupSelector);
    this._mobileLabel = this._mobileToggle ? this._mobileToggle.querySelector(this._config.mobileLabelSelector) : null;
    this._mobileOpen = false;
    this._sizeSelected = false;

    this._handleOption = this._handleOption.bind(this);
    this._handleMobileToggle = this._handleMobileToggle.bind(this);
    this._handleMobileOutsideClick = this._handleMobileOutsideClick.bind(this);
    this._init();
  }

  /** @private */
  _init() {
    this._container.addEventListener('click', this._handleOption);
    if (this._mobileToggle) this._mobileToggle.addEventListener('click', this._handleMobileToggle);

    this._addToCartFixed = document.querySelector('[data-add-to-cart-fixed]');
    if (this._addToCartFixed) {
      this._addToCartFixed.addEventListener('click', () => {
        if (!this._addToCart) return;
        if (this._addToCart.getAttribute('type') === 'submit') {
          this._addToCart.click();
        } else {
          // Tamanho não selecionado: rola até o seletor
          this._container.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  }

  /**
   * Seleção de opção
   * @private
   */
  _handleOption(e) {
    var option = e.target.closest(this._config.optionSelector);
    if (!option || option.disabled) return;

    var variantId = option.dataset.variantId;
    var isUnavailable = option.classList.contains('sizeSelector__option--unavailable');

    // Atualiza input hidden do formulário e dispara change para apps externos (ex: Restock Alert)
    if (this._input) {
      this._input.value = variantId;
      this._input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Atualiza URL com ?variant= para que apps leiam a variante selecionada
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    history.replaceState(Object.assign({}, history.state), '', url.toString());

    // Marca opção ativa em todos os botões (desktop + popup mobile)
    this._container.querySelectorAll(this._config.optionSelector).forEach(function (opt) {
      opt.classList.toggle('sizeSelector__option--active', opt.dataset.variantId === variantId);
    });

    // Atualiza botão de adicionar ao carrinho
    if (this._addToCart) {
      this._sizeSelected = true;
      var addToCartText = this._addToCart.dataset.textAdd || this._config.addToCartText;
      if (isUnavailable) {
        this._addToCart.textContent = this._addToCart.dataset.textNotify || "Notify Me";
        this._addToCart.setAttribute('type', 'button');
        this._addToCart.dataset.notifyMode = 'true';
        this._addToCart.classList.remove('bg-black');
        this._addToCart.classList.add('bg-black');
      } else {
        this._addToCart.textContent = addToCartText;
        this._addToCart.setAttribute('type', 'submit');
        delete this._addToCart.dataset.notifyMode;
        this._addToCart.classList.remove('bg-black');
        this._addToCart.classList.add('bg-black');
      }
    }

    // Sincroniza botão fixo mobile
    if (this._addToCartFixed) {
      var fixedAddText = this._addToCartFixed.dataset.textAdd || this._config.addToCartText;
      if (isUnavailable) {
        this._addToCartFixed.textContent = this._addToCartFixed.dataset.textNotify || "Notify Me";
        this._addToCartFixed.dataset.notifyMode = 'true';
        this._addToCartFixed.classList.remove('bg-black');
        this._addToCartFixed.classList.add('bg-gray-400');
      } else {
        this._addToCartFixed.textContent = fixedAddText;
        delete this._addToCartFixed.dataset.notifyMode;
        this._addToCartFixed.classList.remove('bg-gray-400');
        this._addToCartFixed.classList.add('bg-black');
      }
    }

    // Mobile: atualiza label e fecha popup
    if (this._mobileOpen) {
      if (this._mobileLabel) this._mobileLabel.textContent = option.textContent.trim();
      this._closeMobilePopup();
    }
  }

  /** @private */
  _handleMobileToggle(e) {
    e.stopPropagation();
    this._mobileOpen ? this._closeMobilePopup() : this._openMobilePopup();
  }

  /** @private */
  _openMobilePopup() {
    if (!this._mobilePopup || !this._mobileToggle) return;
    var rect = this._mobileToggle.getBoundingClientRect();
    this._mobilePopup.style.bottom = (window.innerHeight - rect.top + 4) + 'px';
    this._mobilePopup.style.left = rect.left + 'px';
    this._mobilePopup.style.width = rect.width + 'px';
    this._mobilePopup.classList.add('is-open');
    this._mobileToggle.setAttribute('aria-expanded', 'true');
    this._mobileOpen = true;
    document.addEventListener('click', this._handleMobileOutsideClick);
  }

  /** @private */
  _closeMobilePopup() {
    if (!this._mobilePopup || !this._mobileToggle) return;
    this._mobilePopup.classList.remove('is-open');
    this._mobileToggle.setAttribute('aria-expanded', 'false');
    this._mobileOpen = false;
    document.removeEventListener('click', this._handleMobileOutsideClick);
  }

  /** @private */
  _handleMobileOutsideClick(e) {
    if (!this._mobilePopup.contains(e.target) && !this._mobileToggle.contains(e.target)) {
      this._closeMobilePopup();
    }
  }

  destroy() {
    if (this._container) this._container.removeEventListener('click', this._handleOption);
    if (this._mobileToggle) this._mobileToggle.removeEventListener('click', this._handleMobileToggle);
    document.removeEventListener('click', this._handleMobileOutsideClick);
  }
}

/* ==========================================================================
   ADD TO CART CONTROLLER
   Intercepta o submit do formulário de produto e delega ao CartManager,
   garantindo que o cart drawer abra em vez de navegar para a página do carrinho
   ========================================================================== */

class AddToCartController {
  /**
   * @param {HTMLElement} wrapper - Elemento [data-product-wrapper]
   * @param {Object} config - Configurações
   * @param {string} config.formSelector - Seletor do formulário de produto
   * @param {string} config.inputSelector - Seletor do input hidden de variante
   * @param {string} config.buttonSelector - Seletor do botão de adicionar ao carrinho
   */
  constructor(wrapper, config = {}) {
    this._wrapper = wrapper;
    this._config = {
      formSelector: 'form',
      inputSelector: '[data-size-input]',
      buttonSelector: '[data-add-to-cart]',
      ...config
    };

    this._form = this._wrapper.querySelector(this._config.formSelector);
    this._input = this._wrapper.querySelector(this._config.inputSelector);
    this._button = this._wrapper.querySelector(this._config.buttonSelector);

    this._handleSubmit = this._handleSubmit.bind(this);
    this._init();
  }

  /** @private */
  _init() {
    if (!this._form) return;
    this._form.addEventListener('submit', this._handleSubmit);
  }

  /**
   * Intercepta o submit nativo, delega ao CartManager para abrir o drawer
   * @private
   */
  async _handleSubmit(e) {
    e.preventDefault();

    var variantId = this._input ? this._input.value : null;
    if (!variantId || !window.CartManager) return;

    await window.CartManager.addToCart(variantId, { button: this._button });
  }

  destroy() {
    if (this._form) {
      this._form.removeEventListener('submit', this._handleSubmit);
    }
  }
}

/* ==========================================================================
   SHIPPING CALCULATOR CONTROLLER
   Gerencia o cálculo de frete na PDP usando API nativa do Shopify
   ========================================================================== */

class ShippingCalculatorController {
  constructor(formEl, config = {}) {
    this._form = formEl;
    this._config = {
      zipSelector: '[data-shipping-zip]',
      provinceSelector: '[data-shipping-province]',
      btnSelector: '[data-shipping-btn]',
      resultsSelector: '[data-shipping-results]',
      inputSizeSelector: '[data-size-input]',
      ...config
    };

    if (!this._form) return;

    this._zipInput = this._form.querySelector(this._config.zipSelector);
    this._provinceInput = this._form.querySelector(this._config.provinceSelector);
    this._btn = this._form.querySelector(this._config.btnSelector);
    this._results = this._form.querySelector(this._config.resultsSelector);

    this._handleCalculate = this._handleCalculate.bind(this);

    this._init();
  }

  _init() {
    if (this._btn) {
      this._btn.addEventListener('click', this._handleCalculate);
    }
  }

  async _handleCalculate() {
    const zip = this._zipInput.value.trim();
    if (zip.length < 3) {
      this._results.innerHTML = '<p class="text-red-500 text-[1rem]">Please enter a valid zip code.</p>';
      return;
    }

    const countrySelect = this._form.querySelector('.infoShipping__countrySelect');
    const country = countrySelect ? countrySelect.value : '';

    const originalBtnText = this._btn.textContent;
    this._btn.textContent = 'Calculating...';
    this._results.innerHTML = '';
    this._results.classList.remove('opacity-30');

    try {
      const sizeInput = document.querySelector(this._config.inputSizeSelector);
      const variantId = sizeInput ? sizeInput.value : null;

      if (!variantId) {
        this._results.innerHTML = '<p class="text-red-500 text-[1rem]">Error: Product variant not selected.</p>';
        return;
      }

      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      const existingItem = variantId ? cart.items.find((i) => i.variant_id == variantId) : null;
      const existingQty = existingItem ? existingItem.quantity : 0;
      let added = false;

      // Adiciona o produto ao carrinho silenciosamente
      if (variantId) {
        const addRes = await fetch('/cart/add.js?silent=true', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        if (!addRes.ok) {
          const addErr = await addRes.text();
          this._results.innerHTML = `<p class="text-red-500 text-[1rem]">Error adding to cart: ${addErr}</p>`;
          return;
        }
        added = true;
      }

      // Calcula os rates (com polling para apps de terceiros que retornam 202 Accepted)
      const province = this._provinceInput ? this._provinceInput.value.trim() : '';
      let url = `/cart/shipping_rates.json?shipping_address[zip]=${encodeURIComponent(zip)}&shipping_address[country]=${encodeURIComponent(country)}`;
      if (province) {
        url += `&shipping_address[province]=${encodeURIComponent(province)}`;
      }

      let ratesRes = await fetch(url);
      let attempts = 0;

      while (ratesRes.status === 202 && attempts < 15) {
        await new Promise(resolve => setTimeout(resolve, 500)); // Espera 500ms
        ratesRes = await fetch(url);
        attempts++;
      }

      let ratesData;
      try {
        ratesData = await ratesRes.json();
      } catch (e) {
        ratesData = { error: "Failed to parse JSON. HTTP Status: " + ratesRes.status };
      }

      // Restaura o carrinho silenciosamente
      if (added) {
        await fetch('/cart/change.js?silent=true', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: String(variantId), quantity: existingQty })
        });
      }

      if (ratesData.error || ratesData.message || ratesData.status === 'error' || !ratesData.shipping_rates) {
        this._results.innerHTML = `<p class="text-red-500 text-[1rem]">${this._extractErrorMessage(ratesData)}</p>`;
        return;
      }

      this._renderRates(ratesData.shipping_rates);
    } catch (e) {
      console.error('[ShippingCalculatorController] Error:', e);
      this._results.innerHTML = '<p class="text-red-500 text-[1rem]">Error calculating shipping rates.</p>';
    } finally {
      this._btn.textContent = originalBtnText;
    }
  }

  /**
   * Extracts the first human-readable message from a Shopify validation error response.
   * e.g. {"province":["Selecione um estado"]} → "Selecione um estado"
   * @param {Object} data - The parsed JSON response
   * @returns {string}
   */
  _extractErrorMessage(data) {
    if (!data) return 'Error calculating shipping rates.';

    // Plain string error
    if (typeof data.error === 'string') return data.error;
    if (typeof data.message === 'string') return data.message;

    // Shopify validation object: { field: ["message", ...], ... }
    const keys = Object.keys(data);
    for (let i = 0; i < keys.length; i++) {
      const val = data[keys[i]];
      if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
        return val[0];
      }
      if (typeof val === 'string') return val;
    }

    return 'Error calculating shipping rates.';
  }

  _renderRates(rates) {
    if (!rates || rates.length === 0) {
      this._results.innerHTML = '<p class="text-[1rem] text-black/50">No shipping options found for this destination.</p>';
      return;
    }

    const html = rates.map((rate) => {
      const price = parseFloat(rate.price);
      // A loja internacional pode usar outra moeda, então vamos buscar o símbolo ou apenas exibir o valor
      // Como o shopify retorna o valor em decimal mas sem o símbolo, podemos adicionar um fallback ou pegar o currency da loja.
      // Assumindo dólar para internacional:
      const formattedPrice = price === 0 ? 'Free' : '$ ' + price.toFixed(2);

      return `
        <div class="infoShipping__option">
          <div class="infoShipping__optionInfo flex flex-col gap-[0.2rem]">
            <span class="infoShipping__optionName text-[1.1rem] uppercase">${rate.name}</span>
          </div>
          <span class="infoShipping__optionPrice text-[1.1rem] whitespace-nowrap">${formattedPrice}</span>
        </div>
      `;
    }).join('');

    this._results.innerHTML = html;
  }

  destroy() {
    if (this._zipInput) this._zipInput.removeEventListener('input', this._handleInput);
    if (this._btn) this._btn.removeEventListener('click', this._handleCalculate);
  }
}

/* ==========================================================================
   INFO PANEL CONTROLLER
   Gerencia o painel lateral de informações extras (push drawer)
   ========================================================================== */

class InfoPanelController {
  /**
   * @param {Object} config - Configurações
   * @param {string} config.trackSelector   - Seletor do slide track
   * @param {string} config.itemSelector    - Seletor dos gatilhos que abrem o painel
   * @param {string} config.closeSelector   - Seletor do botão de fechar dentro do painel
   * @param {string} config.panelSelector   - Seletor dos containers de painel de conteúdo
   * @param {string} config.titleSelector   - Seletor do elemento de título no header
   * @param {string} config.openClass       - Classe CSS aplicada ao track quando aberto
   * @param {string} config.activeClass     - Classe CSS aplicada ao painel de conteúdo ativo
   */
  constructor(config = {}) {
    this._config = {
      trackSelector: '[data-pdp-info-track]',
      itemSelector: '[data-pdp-info-open]',
      closeSelector: '[data-pdp-info-close]',
      overlaySelector: '[data-pdp-info-overlay]',
      panelSelector: '[data-pdp-panel]',
      titleSelector: '[data-pdp-info-title]',
      openClass: 'is-open',
      activeClass: 'is-active',
      ...config
    };

    this._track = document.querySelector(this._config.trackSelector);
    this._titleEl = null;
    this._isOpen = false;
    this._currentPanel = null;
    this._historyStatePushed = false;

    this._handleItemClick = this._handleItemClick.bind(this);
    this._handleClose = this._handleClose.bind(this);
    this._boundPopStateHandler = this._handlePopState.bind(this);

    this._init();
  }

  /** @private */
  _init() {
    if (!this._track) return;

    this._titleEl = this._track.querySelector(this._config.titleSelector);

    // Delegação: qualquer infoItem no documento abre o painel
    document.addEventListener('click', this._handleItemClick);

    // Botão de fechar (dentro do track, não precisa de delegação)
    var closeBtn = this._track.querySelector(this._config.closeSelector);
    if (closeBtn) closeBtn.addEventListener('click', this._handleClose);

    // Overlay mobile — clique fecha o drawer
    var overlay = this._track.querySelector(this._config.overlaySelector);
    if (overlay) overlay.addEventListener('click', this._handleClose);

    // Botão voltar do navegador (mobile)
    window.addEventListener('popstate', this._boundPopStateHandler);
  }

  /**
   * Handler de clique via delegação — lê o painel alvo e abre
   * @private
   */
  _handleItemClick(e) {
    var item = e.target.closest(this._config.itemSelector);
    if (!item) return;

    var panelKey = item.dataset.pdpInfoPanel;
    if (this._isOpen && this._currentPanel === panelKey) {
      this.close();
    } else {
      this.open(panelKey, item.dataset.pdpInfoPanelTitle);
    }
  }

  /** @private */
  _handleClose() {
    this.close();
  }

  /**
   * Handler para o botão voltar do navegador — fecha o drawer em vez de navegar
   * @private
   */
  _handlePopState() {
    if (this._isOpen && this._historyStatePushed) {
      this._historyStatePushed = false;
      this._closeDrawer();
    }
  }

  /**
   * Exibe o painel correspondente à chave e oculta os demais
   * @param {string|null} panelKey - Valor do atributo data-pdp-panel
   * @private
   */
  _showPanel(panelKey) {
    var activeClass = this._config.activeClass;
    document.querySelectorAll(this._config.panelSelector).forEach(function (panel) {
      panel.classList.remove(activeClass);
    });
    if (!panelKey) return;
    var target = document.querySelector('[data-pdp-panel="' + panelKey + '"]');
    if (target) target.classList.add(activeClass);
  }

  /**
   * Atualiza o texto do título no header
   * @param {string} title
   * @private
   */
  _updateTitle(title) {
    if (this._titleEl) this._titleEl.textContent = title || '';
  }

  /**
   * Abre o painel, exibe o conteúdo correspondente e atualiza o título
   * @param {string} panelKey   - Valor do data-pdp-info-panel do item clicado
   * @param {string} panelTitle - Valor do data-pdp-info-panel-title do item clicado
   */
  open(panelKey, panelTitle) {
    if (!this._track) return;
    this._isOpen = true;
    this._currentPanel = panelKey;
    this._track.classList.add(this._config.openClass);
    this._showPanel(panelKey);
    this._updateTitle(panelTitle);

    // Bloqueia scroll da página no mobile enquanto o drawer está aberto
    if (window.innerWidth < 750) {
      document.body.style.overflow = 'hidden';
    }

    // Adiciona estado ao history para capturar o botão voltar (mobile)
    if (!this._historyStatePushed) {
      history.pushState({ infoPanelOpen: true }, '', window.location.href);
      this._historyStatePushed = true;
    }
  }

  /**
   * Fecha o painel via botão voltar — sem interagir com o history (já avançou).
   * _showPanel(null) é intencionalmente omitido: o conteúdo permanece visível
   * durante a animação de slide-out. O próximo open() sempre define o painel correto.
   * @private
   */
  _closeDrawer() {
    if (!this._track) return;
    this._isOpen = false;
    this._currentPanel = null;
    this._track.classList.remove(this._config.openClass);
    document.body.style.overflow = '';
  }

  /**
   * Fecha o painel. Se o history foi avançado, usa back() para manter
   * a navegação consistente — o popstate chama _closeDrawer() de fato.
   */
  close() {
    if (!this._track) return;
    if (this._historyStatePushed) {
      history.back();
      return;
    }
    this._closeDrawer();
  }

  destroy() {
    document.removeEventListener('click', this._handleItemClick);
    window.removeEventListener('popstate', this._boundPopStateHandler);
    if (this._track) {
      var closeBtn = this._track.querySelector(this._config.closeSelector);
      if (closeBtn) closeBtn.removeEventListener('click', this._handleClose);

      var overlay = this._track.querySelector(this._config.overlaySelector);
      if (overlay) overlay.removeEventListener('click', this._handleClose);
    }
    this._track = null;
  }
}

/* ==========================================================================
   FULLSCREEN CONTROLLER
   Abre imagens em tela cheia com thumbnails, barra de produto e size selector
   ========================================================================== */

class FullscreenController {
  /**
   * @param {HTMLElement} section - Elemento da seção PDP
   * @param {Object} config - Configurações
   * @param {string} config.sectionId - ID da seção Shopify
   */
  constructor(section, config = {}) {
    this._section = section;
    this._config = {
      sectionId: '',
      modalSelector: '[data-pdp-fullscreen]',
      closeSelector: '[data-pdp-fullscreen-close]',
      imageSelector: '[data-pdp-fullscreen-image]',
      thumbSelector: '[data-pdp-fullscreen-thumb]',
      openBtnSelector: '[data-pdp-fullscreen-open]',
      slideTriggerSelector: '[data-pdp-fullscreen-slide-trigger]',
      barToggleSelector: '[data-pdp-fullscreen-bar-toggle]',
      barContentSelector: '[data-pdp-fullscreen-bar-content]',
      prevBtnSelector: '[data-pdp-fullscreen-prev]',
      nextBtnSelector: '[data-pdp-fullscreen-next]',
      sizeSelectorSelector: '[data-pdp-fullscreen-size-selector]',
      sizeToggleSelector: '[data-pdp-fullscreen-size-toggle]',
      sizePopupSelector: '[data-pdp-fullscreen-size-popup]',
      sizeLabelSelector: '[data-pdp-fullscreen-size-label]',
      sizeOptionSelector: '[data-pdp-fullscreen-size-option]',
      swatchesSelector: '[data-pdp-fullscreen-swatches]',
      addToCartSelector: '[data-pdp-fullscreen-add-to-cart]',
      toastSelector: '[data-pdp-fullscreen-toast]',
      ...config
    };

    this._modal = document.querySelector(this._config.modalSelector);
    if (!this._modal) return;

    this._images = this._modal.querySelectorAll(this._config.imageSelector);
    this._thumbs = this._modal.querySelectorAll(this._config.thumbSelector);
    this._closeBtn = this._modal.querySelector(this._config.closeSelector);
    this._prevBtn = this._modal.querySelector(this._config.prevBtnSelector);
    this._nextBtn = this._modal.querySelector(this._config.nextBtnSelector);
    this._barToggles = Array.from(this._modal.querySelectorAll(this._config.barToggleSelector));
    this._barToggle = this._barToggles[this._barToggles.length - 1];
    this._barContent = this._modal.querySelector(this._config.barContentSelector);
    this._sizeSelector = this._modal.querySelector(this._config.sizeSelectorSelector);
    this._sizeToggle = this._sizeSelector ? this._sizeSelector.querySelector(this._config.sizeToggleSelector) : null;
    this._sizePopup = this._modal.querySelector(this._config.sizePopupSelector);
    this._sizeLabel = this._sizeSelector ? this._sizeSelector.querySelector(this._config.sizeLabelSelector) : null;
    this._swatchesContainers = this._modal.querySelectorAll(this._config.swatchesSelector);
    this._addToCartBtns = this._modal.querySelectorAll(this._config.addToCartSelector);
    this._toast = this._modal.querySelector(this._config.toastSelector);
    this._imagesContainer = this._modal.querySelector('.pdpFullscreen__images');

    // Referências na seção principal
    this._mainSizeOptions = this._section.querySelectorAll('[data-size-option]');
    this._mainSizeLabel = this._section.querySelector('[data-size-label]');
    this._mainSizeInput = this._section.querySelector('[data-size-input]');
    this._mainAddToCart = this._section.querySelector('[data-add-to-cart]');

    this._isOpen = false;
    this._barOpen = false;
    this._sizeOpen = false;
    this._sizeSelected = false;
    this._currentIndex = 0;
    this._historyStatePushed = false;
    this._toastTimeout = null;
    this._isZoomed = false;
    this._zoomHistoryPushed = false;
    this._cleaningZoomHistory = false;
    this._panX = 0;
    this._panY = 0;
    this._panStartX = 0;
    this._panStartY = 0;
    this._didPan = false;
    this._lastTapTime = 0;
    this._swipeStartX = null;  // null = sem swipe ativo
    this._swipeStartY = null;
    this._imgPanY = 50;
    // Pinch state
    this._isPinching = false;
    this._initialPinchDistance = 0;
    this._pinchZoomedIn = false;  // evita sair/entrar múltiplas vezes por pinch

    this._handleOpenBtn = this._handleOpenBtn.bind(this);
    this._handleSlideClick = this._handleSlideClick.bind(this);
    this._handleClose = this._handleClose.bind(this);
    this._handleThumb = this._handleThumb.bind(this);
    this._handlePrev = this._handlePrev.bind(this);
    this._handleNext = this._handleNext.bind(this);
    this._handleBarToggle = this._handleBarToggle.bind(this);
    this._handleSizeToggle = this._handleSizeToggle.bind(this);
    this._handleSizeOption = this._handleSizeOption.bind(this);
    this._handleSizeOutsideClick = this._handleSizeOutsideClick.bind(this);
    this._handleAddToCart = this._handleAddToCart.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
    this._handlePopState = this._handlePopState.bind(this);
    this._handleResize = this._handleResize.bind(this);
    this._handleImageClick = this._handleImageClick.bind(this);
    this._handleDoubleTap = this._handleDoubleTap.bind(this);
    this._handlePanStart = this._handlePanStart.bind(this);
    this._handlePanMove = this._handlePanMove.bind(this);
    this._handlePanEnd = this._handlePanEnd.bind(this);
    this._handleWheel = this._handleWheel.bind(this);

    this._init();
  }

  /** @private */
  _init() {
    // Botões de abrir (ícone hover no desktop)
    this._section.querySelectorAll(this._config.openBtnSelector).forEach((btn) => {
      btn.addEventListener('click', this._handleOpenBtn);
    });

    // Tap nos slides mobile
    this._section.querySelectorAll(this._config.slideTriggerSelector).forEach((slide) => {
      slide.addEventListener('click', this._handleSlideClick);
    });

    if (this._closeBtn) this._closeBtn.addEventListener('click', this._handleClose);
    if (this._prevBtn) this._prevBtn.addEventListener('click', this._handlePrev);
    if (this._nextBtn) this._nextBtn.addEventListener('click', this._handleNext);
    this._thumbs.forEach((thumb) => thumb.addEventListener('click', this._handleThumb));
    this._barToggles.forEach((btn) => btn.addEventListener('click', this._handleBarToggle));
    this._modal.addEventListener('click', this._handleSizeOption);
    this._hasMultipleVariants = this._modal.querySelectorAll(this._config.sizeOptionSelector).length > 0;
    this._addToCartBtns.forEach((btn) => btn.addEventListener('click', this._handleAddToCart));

    document.addEventListener('keydown', this._handleKeydown);
    window.addEventListener('popstate', this._handlePopState);
    window.addEventListener('resize', this._handleResize);

    if (this._imagesContainer) {
      this._imagesContainer.addEventListener('touchstart', this._handlePanStart, { passive: true });
      this._imagesContainer.addEventListener('touchmove', this._handlePanMove, { passive: false });
      this._imagesContainer.addEventListener('touchend', this._handlePanEnd);
      this._imagesContainer.addEventListener('touchend', this._handleDoubleTap);
      this._imagesContainer.addEventListener('wheel', this._handleWheel, { passive: false });
      this._imagesContainer.addEventListener('click', this._handleImageClick);
    }
  }

  /** @private */
  _handleOpenBtn(e) {
    var index = parseInt(e.currentTarget.dataset.pdpFullscreenOpen) || 0;
    this.open(index);
  }

  /** @private */
  _handleSlideClick() {
    var splideEl = this._section.querySelector('.splide');
    var index = (splideEl && splideEl.splide) ? splideEl.splide.index : 0;
    this.open(index);
  }

  /** @private */
  _handleClose() {
    this.close();
  }

  /** @private */
  _handleThumb(e) {
    var index = parseInt(e.currentTarget.dataset.pdpFullscreenThumb) || 0;
    this.goTo(index);
  }

  /** @private */
  _handlePrev() {
    var total = this._images.length;
    this.goTo((this._currentIndex - 1 + total) % total);
  }

  /** @private */
  _handleNext() {
    var total = this._images.length;
    this.goTo((this._currentIndex + 1) % total);
  }

  /** @private */
  _handleBarToggle() {
    if (window.innerWidth >= 750) return;
    this._barOpen ? this._closeBar() : this._openBar();
  }

  /** @private */
  _handleSizeToggle(e) {
    e.stopPropagation();
    this._sizeOpen ? this._closeSize() : this._openSize(this._sizeToggle);
  }

  /** @private */
  _handleSizeOption(e) {
    var option = e.target.closest(this._config.sizeOptionSelector);
    if (!option || option.disabled) return;

    var variantId = option.dataset.variantId;
    var isUnavailable = option.classList.contains('pdpFullscreen__sizeChip--na');

    this._modal.querySelectorAll(this._config.sizeOptionSelector).forEach((opt) => {
      opt.classList.remove('is-active-chip');
    });
    option.classList.add('is-active-chip');

    // Dispara a seleção no SizeSelectorController principal — ele sincroniza tudo
    var mainOption = this._section.querySelector('[data-size-option][data-variant-id="' + variantId + '"]');
    if (mainOption) mainOption.click();

    this._sizeSelected = true;

    // Adiciona ao carrinho imediatamente e abre o drawer
    if (!isUnavailable && variantId && window.CartManager) {
      window.CartManager.addToCart(variantId, {}).then((result) => {
        if (result && result.success && window.CartDrawerManager) {
          window.CartDrawerManager.open();
        }
      });
    }
  }

  /** @private */
  _handleSizeOutsideClick(e) {
    if (this._sizeSelector && !this._sizeSelector.contains(e.target)) {
      if (!this._sizePopup || !this._sizePopup.contains(e.target)) {
        this._closeSize();
      }
    }
  }

  /** @private */
  async _handleAddToCart(e) {
    if (!this._sizeSelected && this._hasMultipleVariants) {
      e.stopPropagation();
      if (window.innerWidth < 750 && !this._barOpen) this._openBar();
      return;
    }

    var variantId = this._mainSizeInput ? this._mainSizeInput.value : null;
    if (!variantId || !window.CartManager) {
      if (this._mainAddToCart) this._mainAddToCart.click();
      return;
    }

    var clickedBtn = e.currentTarget;
    this._addToCartBtns.forEach((btn) => { if (btn !== clickedBtn) btn.disabled = true; });

    var result = await window.CartManager.addToCart(variantId, { button: clickedBtn });

    this._addToCartBtns.forEach((btn) => { if (btn !== clickedBtn) btn.disabled = false; });

    if (result.success) this._showToast();
  }

  /** @private */
  _showToast() {
    if (!this._toast) return;
    this._toast.classList.add('is-visible');
    clearTimeout(this._toastTimeout);
    this._toastTimeout = setTimeout(() => {
      this._toast.classList.remove('is-visible');
    }, 2500);
  }

  /** @private */
  _enterZoom() {
    if (this._isZoomed) return;
    this._isZoomed = true;
    this._panX = 0;
    this._panY = 0;
    this._modal.classList.add('is-zoom');

    var activeImage = this._images[this._currentIndex];
    if (activeImage) activeImage.style.transform = 'scale(2) translate(0px, 0px)';

    if (!this._zoomHistoryPushed) {
      history.pushState({ pdpZoomOpen: true }, '', window.location.href);
      this._zoomHistoryPushed = true;
    }
  }

  /**
   * @param {boolean} fromHistory - true quando chamado via popstate (não chama history.back)
   * @private
   */
  _exitZoom(fromHistory) {
    if (!this._isZoomed) return;
    this._isZoomed = false;
    this._panX = 0;
    this._panY = 0;
    this._modal.classList.remove('is-zoom');

    var activeImage = this._images[this._currentIndex];
    if (activeImage) {
      activeImage.style.transform = '';
      activeImage.classList.remove('is-panning');
    }

    if (!fromHistory && this._zoomHistoryPushed) {
      this._zoomHistoryPushed = false;
      this._cleaningZoomHistory = true;
      history.back();
    } else {
      this._zoomHistoryPushed = false;
    }
  }

  /** @private */
  _handleImageClick(e) {
    if (!this._isOpen) return;
    if (window.innerWidth < 750) return;
    if (e.target.closest('button') || e.target.closest('a')) return;
    this._isZoomed ? this._exitZoom(false) : this._enterZoom();
  }

  /** @private */
  _handleDoubleTap(e) {
    if (window.innerWidth >= 750) return;
    // Ignora se foi um gesto de pinch ou de arrasto
    if (this._isPinching || this._didPan) return;

    var now = Date.now();
    if (now - this._lastTapTime < 350) {
      this._lastTapTime = 0;
      this._isZoomed ? this._exitZoom(false) : this._enterZoom();
    } else {
      this._lastTapTime = now;
    }
  }

  /** @private */
  _handlePanStart(e) {
    this._didPan = false;

    if (e.touches.length >= 2) {
      // Inicia gesto de pinch — desativa swipe e pan de dedo único
      this._isPinching = true;
      this._pinchZoomedIn = false;
      this._swipeStartX = null;  // invalida swipe para este toque
      this._swipeStartY = null;
      this._initialPinchDistance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      // Remove pan class caso estivesse em pan single-touch
      var activeImage = this._images[this._currentIndex];
      if (activeImage) activeImage.classList.remove('is-panning');
      return;
    }

    if (e.touches.length !== 1) return;

    // Só registra swipe/pan se não vier de um pinch
    if (!this._isPinching) {
      this._swipeStartX = e.touches[0].clientX;
      this._swipeStartY = e.touches[0].clientY;
    }

    if (!this._isZoomed) return;

    this._panStartX = e.touches[0].clientX - this._panX;
    this._panStartY = e.touches[0].clientY - this._panY;

    var activeImage = this._images[this._currentIndex];
    if (activeImage) activeImage.classList.add('is-panning');
  }

  /** @private */
  _handlePanMove(e) {
    if (this._isPinching && e.touches.length >= 2) {
      e.preventDefault();
      var currentDistance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      var delta = currentDistance - this._initialPinchDistance;

      if (!this._pinchZoomedIn && !this._isZoomed && delta > 20) {
        // Pinch-open: entra no zoom
        this._pinchZoomedIn = true;
        this._enterZoom();
      } else if (!this._pinchZoomedIn && this._isZoomed && delta < -20) {
        // Pinch-close: sai do zoom
        this._pinchZoomedIn = true;
        this._exitZoom(false);
      }
      return;
    }

    // Se ainda há 2+ dedos mas _isPinching foi resetado, ignora
    if (e.touches.length !== 1) return;
    if (!this._isZoomed) return;

    var dx = e.touches[0].clientX - (this._swipeStartX || 0);
    var dy = e.touches[0].clientY - (this._swipeStartY || 0);
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      this._didPan = true;
    }

    e.preventDefault();

    var rawX = e.touches[0].clientX - this._panStartX;
    var rawY = e.touches[0].clientY - this._panStartY;
    var maxX = window.innerWidth * 0.25;
    var maxY = window.innerHeight * 0.25;

    this._panX = Math.max(-maxX, Math.min(maxX, rawX));
    this._panY = Math.max(-maxY, Math.min(maxY, rawY));

    var activeImage = this._images[this._currentIndex];
    if (activeImage) {
      activeImage.style.transform = 'scale(2) translate(' + this._panX + 'px, ' + this._panY + 'px)';
    }
  }

  /** @private */
  _handlePanEnd(e) {
    var wasPinching = this._isPinching;

    // Quando todos os dedos saem, reseta estado de pinch
    if (e.touches.length === 0) {
      this._isPinching = false;
      this._initialPinchDistance = 0;
      this._pinchZoomedIn = false;
    }

    if (this._isZoomed) {
      var activeImage = this._images[this._currentIndex];
      if (activeImage) activeImage.classList.remove('is-panning');
      return;
    }

    // Se o gesto era pinch, nunca dispara swipe de navegação
    if (wasPinching) return;
    if (this._swipeStartX === null) return;
    if (e.changedTouches.length !== 1) return;

    var dx = e.changedTouches[0].clientX - this._swipeStartX;
    var dy = e.changedTouches[0].clientY - this._swipeStartY;

    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      var total = this._images.length;
      if (dx < 0) {
        this.goTo((this._currentIndex + 1) % total);
      } else {
        this.goTo((this._currentIndex - 1 + total) % total);
      }
    }
  }

  /** @private */
  _handleWheel(e) {
    if (window.innerWidth < 750) return;
    e.preventDefault();

    var container = this._images[this._currentIndex];
    if (!container) return;

    var img = container.querySelector('.pdpFullscreen__imageEl');
    if (!img || !img.naturalWidth) return;

    var renderedH = window.innerWidth * (img.naturalHeight / img.naturalWidth);
    if (renderedH <= window.innerHeight) return;

    this._imgPanY = Math.max(0, Math.min(100, this._imgPanY + e.deltaY * 0.05));
    img.style.objectPosition = 'center ' + this._imgPanY + '%';
  }

  /** @private */
  _handleKeydown(e) {
    if (e.key === 'Escape' && this._isOpen) this.close();
  }

  /** @private */
  _handlePopState(e) {
    // Popstate acionado pelo nosso próprio history.back() para limpar estado de zoom — ignorar
    if (this._cleaningZoomHistory) {
      this._cleaningZoomHistory = false;
      return;
    }
    // Botão voltar enquanto zoomed — apenas sai do zoom, fullscreen permanece
    if (this._isZoomed) {
      this._exitZoom(true);
      return;
    }
    if (this._isOpen && this._historyStatePushed) {
      // Se o estado para o qual estamos voltando ainda é o do fullscreen,
      // significa que outro overlay (ex: cart drawer) fechou via history.back() —
      // o fullscreen deve permanecer aberto.
      if (e && e.state && e.state.pdpFullscreenOpen) return;
      this._historyStatePushed = false;
      this._closeModal();
    }
  }

  /**
   * Abre o fullscreen no índice de imagem especificado
   * @param {number} index
   */
  open(index) {
    this._isOpen = true;
    this.goTo(index);
    this._syncSwatches();
    this._modal.classList.add('is-open');
    this._modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    if (!this._historyStatePushed) {
      history.pushState({ pdpFullscreenOpen: true }, '', window.location.href);
      this._historyStatePushed = true;
    }

    document.addEventListener('click', this._handleSizeOutsideClick);
  }

  /**
   * Fecha o fullscreen
   */
  close() {
    if (this._isZoomed && this._zoomHistoryPushed && this._historyStatePushed) {
      // Dois estados no history (zoom + fullscreen) — vai direto para a página
      this._isZoomed = false;
      this._zoomHistoryPushed = false;
      this._historyStatePushed = false;
      this._cleaningZoomHistory = true;
      history.go(-2);
      this._closeModal();
      return;
    }
    if (this._historyStatePushed) {
      history.back();
      return;
    }
    this._closeModal();
  }

  /** @private */
  _closeModal() {
    if (this._isZoomed) this._exitZoom(true);
    this._isOpen = false;
    this._modal.classList.remove('is-open');
    this._modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this._closeBar();
    document.removeEventListener('click', this._handleSizeOutsideClick);

    this._sizeSelected = false;
    var sizeArea = this._modal.querySelector('[data-pdp-fullscreen-size-area]');
    if (sizeArea) sizeArea.classList.remove('is-selected');
    this._modal.querySelectorAll(this._config.sizeOptionSelector).forEach((opt) => {
      opt.classList.remove('is-active-chip');
    });
    this._addToCartBtns.forEach((btn) => {
      btn.textContent = btn.dataset.textSelect || '';
    });
  }

  /**
   * Navega para a imagem do índice especificado.
   * Acima de 1440px exibe duas imagens lado a lado.
   * @param {number} index
   */
  goTo(index) {
    this._currentIndex = index;
    this._imgPanY = 50;

    this._images.forEach((img, i) => {
      img.classList.toggle('is-active', i === index);
      img.classList.remove('is-active--secondary');
      var imgEl = img.querySelector('.pdpFullscreen__imageEl');
      if (imgEl) imgEl.style.objectPosition = '';
    });

    this._thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
    });
  }

  /** @private */
  _handleResize() {
    if (this._isOpen) this.goTo(this._currentIndex);
  }

  /** @private */
  _openBar() {
    this._barOpen = true;
    if (this._barToggle) this._barToggle.setAttribute('aria-expanded', 'true');
    if (this._barContent) this._barContent.classList.add('is-open');
    // Troca ícones
    var plus = this._barToggle ? this._barToggle.querySelector('.pdpFullscreen__barIconPlus') : null;
    var minus = this._barToggle ? this._barToggle.querySelector('.pdpFullscreen__barIconMinus') : null;
    if (plus) plus.style.display = 'none';
    if (minus) minus.style.display = '';
  }

  /** @private */
  _closeBar() {
    this._barOpen = false;
    if (this._barToggle) this._barToggle.setAttribute('aria-expanded', 'false');
    if (this._barContent) this._barContent.classList.remove('is-open');
    var plus = this._barToggle ? this._barToggle.querySelector('.pdpFullscreen__barIconPlus') : null;
    var minus = this._barToggle ? this._barToggle.querySelector('.pdpFullscreen__barIconMinus') : null;
    if (plus) plus.style.display = '';
    if (minus) minus.style.display = 'none';
  }

  /**
   * @private
   * @param {HTMLElement} referenceEl - Elemento de referência para posicionar o popup acima dele
   */
  _openSize(referenceEl) {
    this._sizeOpen = true;
    if (this._sizePopup) {
      if (referenceEl) {
        var rect = referenceEl.getBoundingClientRect();
        this._sizePopup.style.bottom = (window.innerHeight - rect.top + 4) + 'px';
        this._sizePopup.style.left = rect.left + 'px';
        this._sizePopup.style.width = rect.width + 'px';
      }
      this._sizePopup.classList.add('pdpFullscreen__sizePopup--open');
    }
    if (this._sizeToggle) this._sizeToggle.setAttribute('aria-expanded', 'true');
  }

  /** @private */
  _closeSize() {
    this._sizeOpen = false;
    if (this._sizePopup) this._sizePopup.classList.remove('pdpFullscreen__sizePopup--open');
    if (this._sizeToggle) this._sizeToggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * Copia swatches de irmãos já carregados pela seção principal
   * @private
   */
  _syncSwatches() {
    if (!this._swatchesContainers.length) return;

    var mainColorVariants = this._section.querySelector('.pdpMain__colorVariants');
    if (!mainColorVariants) return;

    var siblingSwatches = mainColorVariants.querySelectorAll('a.pdpMain__colorSwatch');
    this._swatchesContainers.forEach((container) => {
      siblingSwatches.forEach((swatch) => {
        var href = swatch.getAttribute('href');
        if (!container.querySelector('a[href="' + href + '"]')) {
          container.appendChild(swatch.cloneNode(true));
        }
      });
    });
  }

  destroy() {
    this._section.querySelectorAll(this._config.openBtnSelector).forEach((btn) => {
      btn.removeEventListener('click', this._handleOpenBtn);
    });
    this._section.querySelectorAll(this._config.slideTriggerSelector).forEach((slide) => {
      slide.removeEventListener('click', this._handleSlideClick);
    });
    if (this._closeBtn) this._closeBtn.removeEventListener('click', this._handleClose);
    if (this._prevBtn) this._prevBtn.removeEventListener('click', this._handlePrev);
    if (this._nextBtn) this._nextBtn.removeEventListener('click', this._handleNext);
    this._thumbs.forEach((thumb) => thumb.removeEventListener('click', this._handleThumb));
    this._barToggles.forEach((btn) => btn.removeEventListener('click', this._handleBarToggle));
    if (this._sizeToggle) this._sizeToggle.removeEventListener('click', this._handleSizeToggle);
    if (this._sizePopup) this._sizePopup.removeEventListener('click', this._handleSizeOption);
    document.removeEventListener('click', this._handleSizeOutsideClick);
    this._addToCartBtns.forEach((btn) => btn.removeEventListener('click', this._handleAddToCart));
    document.removeEventListener('keydown', this._handleKeydown);
    window.removeEventListener('popstate', this._handlePopState);
    window.removeEventListener('resize', this._handleResize);
    if (this._imagesContainer) {
      this._imagesContainer.removeEventListener('touchstart', this._handlePanStart);
      this._imagesContainer.removeEventListener('touchmove', this._handlePanMove);
      this._imagesContainer.removeEventListener('touchend', this._handlePanEnd);
      this._imagesContainer.removeEventListener('touchend', this._handleDoubleTap);
      this._imagesContainer.removeEventListener('wheel', this._handleWheel);
    }
  }
}

/* ==========================================================================
   NOTIFY DRAWER CONTROLLER
   Abre o drawer de avise-me quando o botão está no estado de indisponível,
   movendo o widget do app (da-restock-alerts) para dentro do drawer
   ========================================================================== */

class NotifyDrawerController {
  constructor() {
    this._drawer = document.querySelector('[data-notify-drawer]');
    this._overlay = document.querySelector('[data-notify-overlay]');
    this._content = document.querySelector('[data-notify-drawer-content]');
    this._closeBtn = document.querySelector('[data-notify-close]');

    if (!this._drawer) return;

    this._isOpen = false;
    this._handleButtonClick = this._handleButtonClick.bind(this);
    this._handleClose = this._handleClose.bind(this);

    document.addEventListener('click', this._handleButtonClick);
    if (this._closeBtn) this._closeBtn.addEventListener('click', this._handleClose);
    if (this._overlay) this._overlay.addEventListener('click', this._handleClose);
  }

  /** @private */
  _handleButtonClick(e) {
    var btn = e.target.closest('[data-add-to-cart], [data-add-to-cart-fixed]');
    if (!btn) return;
    if (btn.dataset.notifyMode !== 'true') return;

    e.preventDefault();
    e.stopPropagation();
    this._open();
  }

  /** @private */
  _open() {
    if (!this._drawer) return;
    this._drawer.classList.add('is-open');
    this._drawer.setAttribute('aria-hidden', 'false');
    if (this._overlay) {
      this._overlay.classList.add('is-open');
      this._overlay.setAttribute('aria-hidden', 'false');
    }
    document.body.style.overflow = 'hidden';
    this._isOpen = true;
  }

  /** @private */
  _handleClose() {
    if (!this._drawer) return;
    this._drawer.classList.remove('is-open');
    this._drawer.setAttribute('aria-hidden', 'true');
    if (this._overlay) {
      this._overlay.classList.remove('is-open');
      this._overlay.setAttribute('aria-hidden', 'true');
    }
    document.body.style.overflow = '';
    this._isOpen = false;
  }

  destroy() {
    document.removeEventListener('click', this._handleButtonClick);
    if (this._closeBtn) this._closeBtn.removeEventListener('click', this._handleClose);
    if (this._overlay) this._overlay.removeEventListener('click', this._handleClose);
  }
}

/* ==========================================================================
   PDP MANAGER
   Orquestrador principal - inicializa e coordena todos os módulos
   ========================================================================== */

class PdpManager {
  /**
   * @param {Object} config - Configurações
   * @param {string} config.sectionSelector - Seletor da seção PDP
   * @param {string} config.sectionId - ID da seção Shopify
   */
  constructor(config = {}) {
    this._config = {
      sectionSelector: '.pdpMain--',
      sectionId: '',
      ...config
    };

    this._modules = {};
    this._init();
  }

  /** @private */
  _init() {
    var sectionSelector = this._config.sectionSelector + this._config.sectionId;
    var section = document.querySelector(sectionSelector);
    var extraSection = document.querySelector('.pdpMain__extraInfo--' + this._config.sectionId);

    if (!section) return;

    // Galeria de imagens
    this._modules.gallery = new GalleryObserver(section);

    // Variantes de cor - produto principal
    this._modules.color = new ColorVariantLoader({
      containerSelector: '[data-color-variants-' + this._config.sectionId + ']'
    });

    // Seletores de tamanho e add-to-cart - produto principal
    this._modules.sizeSelectors = [];
    this._modules.addToCart = [];
    var productWrappers = section.querySelectorAll('[data-product-wrapper]');
    productWrappers.forEach((wrapper) => {
      this._modules.sizeSelectors.push(new SizeSelectorController(wrapper));
      this._modules.addToCart.push(new AddToCartController(wrapper));
    });

    // Produto relacionado — buscado via Predictive Search + Section Rendering API
    this._modules.related = new RelatedProductLoader({
      onReady: (wrapper) => {
        // Inicializa seletor de tamanho e add-to-cart para o produto injetado
        this._modules.sizeSelectors.push(new SizeSelectorController(wrapper));
        this._modules.addToCart.push(new AddToCartController(wrapper));

        // Inicializa variantes de cor para o produto injetado (escopo no wrapper)
        this._modules.colorRelated = new ColorVariantLoader({
          containerSelector: '[data-color-variants-' + this._config.sectionId + ']',
          scope: wrapper
        });
      }
    });

    // Botão CTA fixo mobile — fallback para produtos de variante única (sem size selector)
    var fixedBtn = document.querySelector('[data-add-to-cart-fixed]');
    var mainBtn = section.querySelector('[data-add-to-cart]');
    if (fixedBtn && mainBtn && !section.querySelector('[data-size-selector]')) {
      fixedBtn.addEventListener('click', function() { mainBtn.click(); });
    }

    // Oculta a barra fixa mobile quando o botão inline está visível na tela
    var mobileCTABar = document.querySelector('[data-mobile-cta-bar]');
    var inlineCTA = section.querySelector('[data-add-to-cart]');
    if (mobileCTABar && inlineCTA) {
      var ctaBarObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            mobileCTABar.classList.add('is-hidden');
          } else {
            mobileCTABar.classList.remove('is-hidden');
          }
        });
      }, { threshold: 0 });
      ctaBarObserver.observe(inlineCTA);
    }

    // Accordions (gerenciado globalmente pelo accordion-manager.js)
    // Não incluso em this._modules para evitar que destroy() remova o listener global

    // Painel lateral de informações extras
    this._modules.infoPanel = new InfoPanelController();

    // Drawer de avise-me
    this._modules.notifyDrawer = new NotifyDrawerController();

    // Calculadora de Frete
    let shippingForms = [];
    if (section) shippingForms.push(...Array.from(section.querySelectorAll('[data-shipping-form]')));
    if (extraSection) shippingForms.push(...Array.from(extraSection.querySelectorAll('[data-shipping-form]')));
    this._modules.shippingCalculator = shippingForms.map(form => new ShippingCalculatorController(form));

    // Fullscreen de imagens
    this._modules.fullscreen = new FullscreenController(section, {
      sectionId: this._config.sectionId
    });
  }

  destroy() {
    Object.values(this._modules).forEach((mod) => {
      if (Array.isArray(mod)) {
        mod.forEach((m) => { if (m && typeof m.destroy === 'function') m.destroy(); });
      } else if (mod && typeof mod.destroy === 'function') {
        mod.destroy();
      }
    });
    this._modules = {};
  }
}

/* ==========================================================================
   INICIALIZAÇÃO (chamada pela seção via initPdpManager)
   ========================================================================== */

/**
 * Factory para inicializar o PDP Manager com o section ID correto
 * @param {string} sectionId - ID da seção Shopify
 */
function initPdpManager(sectionId) {
  if (window._pdpManager) {
    window._pdpManager.destroy();
  }

  window._pdpManager = new PdpManager({ sectionId: sectionId });
}

// Suporte ao Theme Editor
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[class*="pdpMain--"]');
    if (section) {
      var sectionId = event.detail.sectionId;
      initPdpManager(sectionId);
    }
  });
}

// Export para módulos ES6
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    PdpManager,
    GalleryObserver,
    ColorVariantLoader,
    SizeSelectorController,
    InfoPanelController,
    ShippingCalculatorController
  };
}
